import { Line } from 'react-chartjs-2'
import CustomPaper from '../../../containers/Paper'
import { useContext, useEffect, useState } from 'react'
import StatisticsService from '../../../services/StatisticsService'
import { StatisticsContext } from '../../../context/StatisticsContext'
import { CircularProgress } from '@mui/material'
import { statisticsColors } from '../StatisticsPage'
import CustomDropdown from '../../../components/Dropdown'
import { formatDaysToYearsAndMonths } from '../../../helpers/utils'

interface AverageDaysType {
    year: number
    averageDays: number
}

interface FirstJobDataType {
    totalAverage: number
    averageToGetFirstJobPerYears: AverageDaysType[]
}

interface DaysToGraduateDataType {
    totalAverage: number
    daysToGraduatePerYears: AverageDaysType[]
}

const AcademicTimeChart = () => {
    const { startDate, endDate, carreer } = useContext(StatisticsContext)
    const [years, setYears] = useState<string>('10')
    const [firstJobTimeData, setFirstJobTimeData] = useState<FirstJobDataType>()
    const [daysToGraduateData, setDaysToGraduateData] = useState<DaysToGraduateDataType>()

    useEffect(() => {
        const service = new StatisticsService()
        service.getAverageToGetFirstJob(carreer, startDate, endDate, years).then((response) => {
            if (response.status !== 200) return
            const result: FirstJobDataType = response.data
            if (result) result.averageToGetFirstJobPerYears.reverse()
            setFirstJobTimeData(result)
        })
        service.getDaysToGraduate(carreer, startDate, endDate, years).then((response) => {
            if (response.status !== 200) return
            const result: DaysToGraduateDataType = response.data
            if (result) result.daysToGraduatePerYears.reverse()
            setDaysToGraduateData(result)
        })
    }, [carreer, startDate, endDate, years])

    const getChartData = () => {
        const daysToGraduate = daysToGraduateData?.daysToGraduatePerYears ?? []
        const firstJob = firstJobTimeData?.averageToGetFirstJobPerYears ?? []
        return {
            labels: daysToGraduate.map((x) => x.year),
            datasets: [
                {
                    label: 'Tiempo promedio en encontrar el primer empleo',
                    data: firstJob.map((x) => x.averageDays),
                    backgroundColor: statisticsColors.background,
                    borderColor: statisticsColors.border,
                    borderWidth: 2,
                    pointRadius: 8,
                    pointHoverRadius: 12,
                    tension: 0.3,
                    fill: true,
                },
                {
                    label: 'Tiempo promedio en terminar la carrera',
                    data: daysToGraduate.map((x) => x.averageDays),
                    backgroundColor: statisticsColors.background,
                    borderColor: statisticsColors.border,
                    borderWidth: 2,
                    pointRadius: 8,
                    pointHoverRadius: 12,
                    tension: 0.3,
                    fill: true,
                },
            ],
        }
    }

    const options = {
        scales: {
            y: {
                beginAtZero: true,
                ticks: { stepSize: 1 },
                max:
                    Math.max(
                        ...([
                            ...(daysToGraduateData?.daysToGraduatePerYears.map(
                                (x) => x.averageDays,
                            ) ?? []),
                            ...(firstJobTimeData?.averageToGetFirstJobPerYears.map(
                                (x) => x.averageDays,
                            ) ?? []),
                        ] as number[]),
                    ) * 1.2,
            },
        },
        maintainAspectRatio: false,
        plugins: {
            legend: { display: true },
            tooltip: {
                callbacks: {
                    label(context: any) {
                        const label: string = context.dataset.label
                        const value = context.raw
                        return `${label}: ${formatDaysToYearsAndMonths(value)} (${value} días)`
                    },
                },
            },
        },
    }

    return (
        <CustomPaper className={`flex flex-col gap-2 h-full md:h-[450px] text-primary`}>
            <div className="flex w-full items-start gap-2 md:gap-0 justify-between">
                <span className="w-[calc(100vw-175px)] md:w-fit text-lg font-semibold">
                    Análisis de tiempos académicos
                </span>
                <CustomDropdown
                    className="w-[100px] h-[40px]"
                    label="Cantidad"
                    value={years}
                    onChange={(e) => setYears(e.target.value as string)}
                    data={['5', '10', '15', '20']}
                />
            </div>
            <div className="flex justify-center items-center h-full">
                {firstJobTimeData && daysToGraduateData ? (
                    <div className="flex flex-col gap-4 w-full h-full">
                        <div className="flex flex-col gap-2 border-2 border-solid border-primary rounded-lg p-1">
                            <span>
                                Tiempo promedio en terminar la carrera:{' '}
                                <b>
                                    {formatDaysToYearsAndMonths(daysToGraduateData?.totalAverage)}
                                </b>
                            </span>
                            <span>
                                Tiempo promedio en encontrar el primer empleo:{' '}
                                <b>{formatDaysToYearsAndMonths(firstJobTimeData?.totalAverage)}</b>
                            </span>
                        </div>
                        <div className="h-full">
                            <Line
                                data={getChartData()}
                                options={options}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="w-full h-full flex justify-center items-center">
                        <CircularProgress
                            className="m-2 md:m-5 text-primary"
                            size={64}
                        />
                    </div>
                )}
            </div>
        </CustomPaper>
    )
}

export default AcademicTimeChart
